class ProductSelector extends HTMLElement {
  constructor() {
    super();

    this.inputWrapper = '[data-input-wrapper]';
    this.variants = JSON.parse(this.querySelector('[type="application/json"]').textContent || '[]');

    this.form = document.querySelector(`#ProductForm-${this.dataset.sectionId}`);
    this.purchaseOptions = document.querySelector(`#Product-Purchase-Options-${this.dataset.sectionId}`);
    this.productBar = document.querySelector(`#ProductBar-${this.dataset.sectionId}`);

    // dataset.installmentsFormId can be a raw id or a number; normalize to a selector
    const instId = this.dataset.installmentsFormId || '';
    const instSelector = instId ? (String(instId).startsWith('#') ? instId : `#Product-Installments-${instId}`) : null;
    this.installmentsForm = instSelector ? document.querySelector(instSelector) : null;

    this.addEventListener('change', this.onVariantChange.bind(this));

    this.updateOptions();
    this.updateVariantStatuses();
    this.updateVariant();
    this.initUpdateMedia();

    if (this.form && this.currentVariant) {
      this.form.toggleAddButton(!this.currentVariant.available, '');
    }
  }

  updateVariantStatuses() {
    const firstWrapper = this.querySelector(this.inputWrapper);
    if (!firstWrapper) return;

    const optionOneChecked = firstWrapper.querySelector(':checked');
    if (!optionOneChecked) return;

    const optionOneValue = optionOneChecked.value;
    const selectedOptionOneVariants = this.variants.filter(variant => optionOneValue === variant.option1);
    const inputWrappers = [...this.querySelectorAll(this.inputWrapper)];

    inputWrappers.forEach((option, index) => {
      if (index === 0) return;

      const prevChecked = inputWrappers[index - 1].querySelector(':checked');
      if (!prevChecked) return;

      const previousOptionSelected = prevChecked.value;
      const listOfAvailableOptions = selectedOptionOneVariants
        .filter(variant => variant.available && variant[`option${index}`] === previousOptionSelected)
        .map(v => v[`option${index + 1}`]);

      const optionInputs = [...option.querySelectorAll('input[type="radio"], option')];

      optionInputs.forEach(input => {
        const value = input.getAttribute('value');
        const inputIsDisabled = !listOfAvailableOptions.includes(value);

        if (!input.getAttribute('data-stock')) {
          input.disabled = inputIsDisabled;
        }

        // Mirror disabled state to dropdown buttons, if present
        if (input.tagName.toLowerCase() === 'option') {
          const escapedValue = input.value.replace(/"/g, '\\"');
          const selector = `button[data-value="${escapedValue}"]`;
          const button = input.closest('dropdown-input')?.querySelector(selector);
          if (button) button.disabled = inputIsDisabled;
        }

        // Sync the sticky product bar options
        if (!this.productBar) return;
        const inputId = input.dataset.id || input.id;
        const productBarInput = this.productBar.querySelector(`option[data-id="${inputId}"]`);
        if (productBarInput) productBarInput.disabled = inputIsDisabled;
      });
    });
  }

  onVariantChange(event) {
    if (event.target.type === 'number') return;

    this.updateOptions();
    if (this.currentVariant === this.getVariantData()) return;

    this.updateVariant();
    this.updateVariantStatuses();
    this.productBarUpdateOptions();
    this.updatePickupAvailability();

    if (this.form) {
      this.form.toggleAddButton(false, '');
      this.form.handleErrorMessage();
    }

    if (!this.currentVariant) {
      if (this.form) this.form.toggleAddButton(true, '');
      this.setUnavailable();
      return;
    }

    if (!this.currentVariant.available && this.form) {
      this.form.toggleAddButton(true, window.variantStrings.soldOut);
    }

    this.updateMedia();
    this.updateURL();
    this.updateVariantInput(); // ✅ keep all hidden inputs in sync
    this.renderProductInfo();
  }

  updateOptions() {
    this.querySelectorAll('input[type="radio"]').forEach(input => {
      input.nextElementSibling?.classList.toggle('is-active', input.checked);
    });

    this.options = Array.from(
      this.querySelectorAll('input[type="radio"]:checked, select'),
      el => ({ name: el.dataset.name || el.name, value: el.value })
    );
  }

  productBarUpdateOptions() {
    if (!this.productBar) return;

    Array.from(this.productBar.querySelectorAll('select')).forEach(select => {
      const match = this.options.find(o => o.name === select.name);
      if (match) select.value = match.value;
    });

    this.productBar.querySelectorAll('dropdown-input').forEach(dropdown => dropdown.update?.());
  }

  updateInventoryNotice(html) {
    const destination = document.querySelector('[data-inventory-notice]');
    const source = html.querySelector('[data-inventory-notice]');
    if (source && destination) destination.innerHTML = source.innerHTML;

    const inventoryQuantityMatch = source?.textContent.match(/\d+/);
    const inventoryQuantity = inventoryQuantityMatch ? parseInt(inventoryQuantityMatch[0], 10) : 0;
    this.updateQuantityInput(inventoryQuantity);
  }

  updateQuantityInput(inventoryQuantity) {
    const selectedVariantData = this.getVariantData();
    const quantityInput = document.querySelector('.product-selector__quantity quantity-input input');

    let productUsesInventory = true;
    if (selectedVariantData && selectedVariantData.inventory_management === null) productUsesInventory = false;
    if (selectedVariantData && selectedVariantData.inventory_quantity !== null && inventoryQuantity === 0 && selectedVariantData.available === true) {
      productUsesInventory = false;
    }

    if (!quantityInput) return;

    if (productUsesInventory) {
      quantityInput.setAttribute('max', inventoryQuantity);
      if (+quantityInput.value > inventoryQuantity) quantityInput.value = inventoryQuantity;
    } else {
      quantityInput.setAttribute('max', '');
    }
  }

  updatePickupAvailability() {
    const pickUpAvailabilityNode = document.querySelector('pickup-availability');
    if (!pickUpAvailabilityNode || !this.currentVariant) return;

    pickUpAvailabilityNode.dataset.variantId = this.currentVariant.id;

    if (this.currentVariant.available) {
      pickUpAvailabilityNode.setAttribute('available', '');
      pickUpAvailabilityNode.fetchAvailability(pickUpAvailabilityNode.dataset.variantId);
      return;
    }

    pickUpAvailabilityNode.removeAttribute('available');
    pickUpAvailabilityNode.innerHTML = '';
  }

  updateVariant() {
    this.currentVariant = this.getVariantData();
  }

  getVariantData() {
    if (!this.options || this.options.length === 0) return this.variants[0];
    return this.variants.find(variant =>
      this.options.every(option => {
        const optionIndex = this.options.findIndex(opt => opt.name === option.name) + 1;
        return variant[`option${optionIndex}`] === option.value;
      })
    );
  }

  initUpdateMedia() {
    const urlParams = new URLSearchParams(window.location.search);
    const variantIdFromUrl = parseInt(urlParams.get('variant'), 10);

    if (variantIdFromUrl) {
      this.currentVariant = this.variants.find(variant => variant.id === variantIdFromUrl);
      if (this.currentVariant) this.updateMedia();
    }
  }

  updateMedia() {
    if (!this.currentVariant?.featured_media) return;
    const productMedia = document.querySelector('product-media');
    productMedia?.setActiveMedia(this.currentVariant.featured_media.id);
  }

  updateURL(sellingPlanId) {
    if (!this.currentVariant) return;
    const params = new URLSearchParams(window.location.search);
    params[params.has('variant') ? 'set' : 'append']('variant', this.currentVariant.id);

    if (sellingPlanId) {
      params[params.has('selling_plan') ? 'set' : 'append']('selling_plan', sellingPlanId);
    } else {
      params.delete('selling_plan');
    }

    window.history.replaceState({}, '', `${this.dataset.url}?${params.toString()}`);
  }

  // ✅ Update ALL relevant hidden variant id inputs (picker, form, installments, product bar)
  updateVariantInput() {
    if (!this.currentVariant) return;

    const inputs = [
      this.querySelector('input[name="id"]'),                       // inside picker
      this.form?.querySelector('input[name="id"]'),                 // inside Add to cart form
      this.installmentsForm?.querySelector('input[name="id"]'),     // installments form (if present)
      this.productBar?.querySelector('input[name="id"]')            // product bar (if present)
    ].filter(Boolean);

    inputs.forEach(input => {
      input.value = this.currentVariant.id;
      input.dispatchEvent(new Event('change', { bubbles: true }));
    });
  }

  setUnavailable() {
    const submitButton = this.form?.submitButton;
    const price = document.querySelector(`#Product-Price-${this.dataset.sectionId}`);
    if (!submitButton) return;
    submitButton.textContent = window.variantStrings.unavailable;
    if (price) price.classList.add('visibility-hidden');
  }

  renderProductInfo() {
    if (!this.currentVariant) return;

    const params = new URLSearchParams(window.location.search);
    params[params.has('variant') ? 'set' : 'append']('variant', this.currentVariant.id);
    params.append('section_id', this.dataset.sectionId);

    fetch(`${this.dataset.url}?${params.toString()}`)
      .then(response => response.text())
      .then(responseText => {
        const html = new DOMParser().parseFromString(responseText, 'text/html');

        [`#Product-Price-${this.dataset.sectionId}`, `#Product-Purchase-Options-${this.dataset.sectionId}`].forEach(selector => {
          const destination = document.querySelector(selector);
          const source = html.querySelector(selector);

          this.updateInventoryNotice(html);

          if (destination && source) {
            destination.classList.remove('visibility-hidden');
            destination.innerHTML = source.innerHTML;
          }
        });
      });
  }
}

customElements.define('product-selector', ProductSelector);
